import React, { useState } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { Asset } from "@iventis/domain-model/model/asset";
import { useQuery } from "@tanstack/react-query";
import { useSubjectContext } from "../../state/subjects-context";
import { MapBackground } from "../../types/map-background";

export function BackgroundSelectorComponent({ getMapBackgrounds }: { getMapBackgrounds?: () => Promise<Asset[]> }) {
    const { backgroundSelected$ } = useSubjectContext();

    const mapboxStandardBackground: MapBackground = {
        id: "mapbox-standard",
        name: "Mapbox Standard",
        url: "mapbox://styles/mapbox/standard",
    };

    const [background, setBackground] = useState<MapBackground>(mapboxStandardBackground);

    const { data: backgrounds = [mapboxStandardBackground] } = useQuery({
        queryKey: ["mapBackgrounds"],
        queryFn: async () => {
            const results = await getMapBackgrounds?.();
            const mapBackgrounds: MapBackground[] = results.map((result) => ({
                id: result.id,
                name: result.name,
                url: `${result.assetUrl}&${result.authoritySignature}`,
            }));
            return [mapboxStandardBackground, ...mapBackgrounds];
        },
        enabled: !!getMapBackgrounds,
    });

    const handleBackgroundChange = (value: MapBackground) => {
        setBackground(value);
        backgroundSelected$.next(value);
    };

    const getOptionLabel = (selectedBackground: MapBackground) => {
        const foundBackground = backgrounds.find((background) => background.id === selectedBackground.id);
        if (foundBackground == null) {
            throw new Error(`Background ${background.id} - ${background.name} can't be found`);
        }
        return foundBackground.name;
    };

    return (
        <Autocomplete
            value={background}
            options={backgrounds}
            getOptionLabel={getOptionLabel}
            onChange={(_, value) => handleBackgroundChange(value)}
            disableClearable
            sx={{ width: "100%" }}
            renderInput={(params) => (
                <TextField
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...params}
                />
            )}
        />
    );
}
